/**
 * Thread ACL hook — fetches and mutates `/api/auth/threads`.
 *
 * The ACL is the source of truth for which sessions a user can see.
 * `useSessions` joins the OpenClaw sessions list against this ACL to
 * decide what to render in the sidebar.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ThreadAcl, PublicUser } from '../types/gateway';

const API_BASE = '/api/auth';
const TOKEN_KEY = 'foreman-auth-token';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function jsonFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(API_BASE + path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export interface UseThreadAclReturn {
  /** ACL rows visible to the current user. */
  acl: ThreadAcl[];
  /** Map for O(1) lookup by sessionKey. */
  aclByKey: Map<string, ThreadAcl>;
  /** Public user roster (for share/mention pickers). */
  users: PublicUser[];
  loading: boolean;
  /** Refresh both lists from the server. */
  refresh: () => Promise<void>;
  /** Create an ACL row for a new session. Caller becomes owner. */
  createAcl: (sessionKey: string, opts?: { name?: string; sharedWith?: string[] | '*' }) => Promise<ThreadAcl>;
  /** Owner-only: replace sharedWith. */
  shareAcl: (sessionKey: string, sharedWith: string[] | '*') => Promise<ThreadAcl>;
  /** Member rename. Updates `thread_acl.name` (1–120 chars). */
  renameAcl: (sessionKey: string, name: string) => Promise<ThreadAcl>;
  /** Owner-only: delete ACL row. The OpenClaw session itself is unaffected. */
  deleteAcl: (sessionKey: string) => Promise<void>;
  /** Owner-only: generate a signed invite URL valid for `expiresHours` hours. */
  createInviteLink: (sessionKey: string, expiresHours: number) => Promise<string>;
  /** Accept an invite token (caller must be authenticated). Returns the joined ACL. */
  acceptInvite: (token: string) => Promise<{ thread: ThreadAcl; alreadyMember: boolean }>;
}

export function useThreadAcl(currentUser: string | null | undefined): UseThreadAclReturn {
  const [acl, setAcl] = useState<ThreadAcl[]>([]);
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!currentUser) {
      setAcl([]);
      setUsers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [aclRes, usersRes] = await Promise.all([
        jsonFetch<{ threads: ThreadAcl[] }>('/threads'),
        jsonFetch<{ users: PublicUser[] }>('/users'),
      ]);
      setAcl(aclRes.threads || []);
      setUsers(usersRes.users || []);
    } catch (err) {
      console.error('[useThreadAcl] refresh failed:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createAcl = useCallback(async (sessionKey: string, opts: { name?: string; sharedWith?: string[] | '*' } = {}) => {
    const res = await jsonFetch<{ thread: ThreadAcl }>('/threads', {
      method: 'POST',
      body: JSON.stringify({
        sessionKey,
        name: opts.name,
        sharedWith: Array.isArray(opts.sharedWith) ? opts.sharedWith : [],
      }),
    });
    setAcl((prev) => [...prev.filter((a) => a.sessionKey !== sessionKey), res.thread]);
    return res.thread;
  }, []);

  const shareAcl = useCallback(async (sessionKey: string, sharedWith: string[] | '*') => {
    const res = await jsonFetch<{ thread: ThreadAcl }>(`/threads/${encodeURIComponent(sessionKey)}/share`, {
      method: 'PATCH',
      body: JSON.stringify({ sharedWith }),
    });
    setAcl((prev) => prev.map((a) => (a.sessionKey === sessionKey ? res.thread : a)));
    return res.thread;
  }, []);

  const renameAcl = useCallback(async (sessionKey: string, name: string) => {
    const res = await jsonFetch<{ thread: ThreadAcl }>(
      `/threads/${encodeURIComponent(sessionKey)}/rename`,
      { method: 'PATCH', body: JSON.stringify({ name }) }
    );
    setAcl((prev) => prev.map((a) => (a.sessionKey === sessionKey ? res.thread : a)));
    return res.thread;
  }, []);

  const deleteAcl = useCallback(async (sessionKey: string) => {
    await jsonFetch<void>(`/threads/${encodeURIComponent(sessionKey)}`, { method: 'DELETE' });
    setAcl((prev) => prev.filter((a) => a.sessionKey !== sessionKey));
  }, []);

  const createInviteLink = useCallback(async (sessionKey: string, expiresHours: number) => {
    const res = await jsonFetch<{ url: string }>(
      `/threads/${encodeURIComponent(sessionKey)}/invite-link`,
      { method: 'POST', body: JSON.stringify({ expiresHours }) }
    );
    return res.url;
  }, []);

  const acceptInvite = useCallback(async (token: string) => {
    const res = await jsonFetch<{ thread: ThreadAcl; alreadyMember: boolean }>(
      '/threads/accept-invite',
      { method: 'POST', body: JSON.stringify({ token }) }
    );
    setAcl((prev) => {
      if (prev.find((a) => a.sessionKey === res.thread.sessionKey)) {
        return prev.map((a) => (a.sessionKey === res.thread.sessionKey ? res.thread : a));
      }
      return [...prev, res.thread];
    });
    return res;
  }, []);

  const aclByKey = new Map(acl.map((a) => [a.sessionKey, a]));

  return { acl, aclByKey, users, loading, refresh, createAcl, shareAcl, renameAcl, deleteAcl, createInviteLink, acceptInvite };
}
